import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, selectIsAdmin, selectIsPending } from '../store/authStore';
import { 
  FiCalendar, 
  FiUser, 
  FiLogOut, 
  FiHome, 
  FiMenu, 
  FiX,
  FiBarChart2,
  FiUsers,
  FiClock,
  FiSettings 
} from 'react-icons/fi';

function Layout() {
  const { user, logout } = useAuthStore();
  const isAdmin = useAuthStore(selectIsAdmin);
  const isPending = useAuthStore(selectIsPending);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [meetingRoom, setMeetingRoom] = useState(null);

  useEffect(() => {
    // Fetch meeting room info
    const fetchMeetingRoom = async () => {
      try {
        // This would be an API call, but for now we'll use static data
        setMeetingRoom({
          name: 'Zasedací místnost Řipská',
          address: 'Řipská 15, Praha 3'
        });
      } catch (error) {
        console.error('Error fetching meeting room:', error);
      }
    };
    fetchMeetingRoom();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isAdminRoute = location.pathname.startsWith('/admin');

  // Navigation items for regular users
  const userNavItems = [
    { to: '/', label: 'Kalendář', icon: <FiCalendar className="mr-2" /> },
    { to: '/reservations', label: 'Moje rezervace', icon: <FiClock className="mr-2" /> },
    { to: '/profile', label: 'Profil', icon: <FiUser className="mr-2" /> }
  ];

  // Navigation items for admins
  const adminNavItems = [
    { to: '/admin', label: 'Admin Dashboard', icon: <FiHome className="mr-2" /> },
    { to: '/admin/users', label: 'Uživatelé', icon: <FiUsers className="mr-2" /> },
    { to: '/admin/reservations', label: 'Rezervace', icon: <FiCalendar className="mr-2" /> },
    { to: '/admin/statistics', label: 'Statistiky', icon: <FiBarChart2 className="mr-2" /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and meeting room info */}
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <FiCalendar className="text-white text-lg" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Rezervační systém</h1>
                  {meetingRoom && (
                    <p className="text-sm text-gray-500">{meetingRoom.address}</p>
                  )}
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {userNavItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.to)
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}

              {isAdmin && (
                <div className="relative">
                  <button
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isAdminRoute
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <FiSettings className="mr-2" />
                    Admin
                  </button>
                </div>
              )}

              {/* User menu */}
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-500">
                  {user?.firstName} {user?.lastName}
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                >
                  <FiLogOut className="mr-2" />
                  Odhlásit
                </button>
              </div>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              <FiMenu className="text-xl" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="px-4 py-4 space-y-2">
            {userNavItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}

            {isAdmin && (
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pb-2">
                  Admin
                </p>
                {adminNavItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.to)
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ))}
              </div>
            )}

            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors w-full"
              >
                <FiLogOut className="mr-2" />
                Odhlásit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>

      {/* Admin sidebar for larger screens */}
      {isAdmin && !mobileMenuOpen && (
        <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white shadow-sm z-10">
          <div className="p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Admin Menu
            </h3>
            <nav className="space-y-1">
              {adminNavItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.to)
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </aside>
      )}

      {/* Admin content padding for sidebar */}
      {isAdmin && !mobileMenuOpen && (
        <div className="hidden lg:block lg:ml-64"></div>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Rezervační systém - Řipská 15, Praha 3
        </div>
      </footer>
    </div>
  );
}

export default Layout;
